import { Search } from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/lib/utils';
import { getSearchShortcutLabel } from '../hooks/use-global-search-hotkey';

export interface GlobalSearchTriggerProps {
  onOpen: () => void;
  className?: string;
}

/** Compact header pill — opens the Spotlight / command palette. */
export function GlobalSearchTrigger({ onOpen, className }: GlobalSearchTriggerProps): JSX.Element {
  const shortcut = useMemo(() => getSearchShortcutLabel(), []);

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onOpen}
      className={cn(
        'global-search-trigger group hidden h-10 w-[260px] items-center justify-start gap-2.5 rounded-xl bg-white/80 px-3 text-ink-500',
        'hover:bg-white hover:text-ink-600',
        'focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        'md:inline-flex lg:w-[320px]',
        className,
      )}
      aria-label="Search or run a command"
    >
      <Search
        className="size-3.5 shrink-0 text-ink-500 transition-colors group-hover:text-brand-600"
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate text-left text-[13px] font-normal text-ink-500">
        Search or run a command…
      </span>
      <kbd className="hidden shrink-0 rounded-md border border-surface-200 bg-surface-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-ink-500 shadow-sm lg:inline-flex">
        {shortcut}
      </kbd>
    </Button>
  );
}

export interface GlobalSearchMobileButtonProps {
  onOpen: () => void;
}

/** Mobile search icon in the header. */
export function GlobalSearchMobileButton({ onOpen }: GlobalSearchMobileButtonProps): JSX.Element {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-10 rounded-xl border border-surface-200/90 bg-white/70 shadow-soft md:hidden"
      onClick={onOpen}
      aria-label="Search"
      title="Search"
    >
      <Search className="size-[18px]" aria-hidden />
    </Button>
  );
}
